<template>
  <div v-if="visible" class="demo-banner" role="status">
    <v-icon size="18" class="demo-banner__icon">mdi-flask-outline</v-icon>
    <span class="demo-banner__text">{{ $t("ncrm.demoData.banner.text") }}</span>
    <v-btn
      v-if="canDelete"
      class="demo-banner__action"
      variant="text"
      size="small"
      density="comfortable"
      @click="openDialog"
    >
      {{ $t("ncrm.demoData.banner.action") }}
    </v-btn>
  </div>

  <v-dialog v-model="dialogOpen" max-width="520" persistent>
    <v-card class="demo-banner__dialog">
      <v-card-title>{{ $t("ncrm.demoData.dialog.title") }}</v-card-title>
      <v-card-text>
        <p class="mb-3">{{ $t("ncrm.demoData.dialog.body") }}</p>

        <v-alert type="warning" density="compact" class="mb-4">
          {{ $t("ncrm.demoData.dialog.warning") }}
        </v-alert>

        <v-alert v-if="errorMessage" type="error" density="compact" class="mb-4">
          {{ errorMessage }}
        </v-alert>
        <v-alert v-if="retainedMessage" type="info" density="compact" class="mb-4">
          {{ retainedMessage }}
        </v-alert>

        <v-text-field
          v-model="confirmation"
          :label="$t('ncrm.demoData.dialog.confirmationLabel', { keyword })"
          :placeholder="keyword"
          autocomplete="off"
          hide-details="auto"
          @keyup.enter="confirmed && submit()"
        />
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn variant="text" :disabled="deleting" @click="closeDialog">
          {{ $t("app.actions.cancel") }}
        </v-btn>
        <v-btn color="error" variant="flat" :loading="deleting" :disabled="!confirmed" @click="submit">
          {{ $t("ncrm.demoData.dialog.confirm") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import {
  canDeleteDemoData,
  deleteDemoData,
  fetchDemoDataStatus,
} from "./ncrmDemoDataApi.mjs";

const DEFAULT_KEYWORD = "DELETE";

export default {
  name: "NcrmDemoDataBanner",
  data() {
    return {
      status: null,
      dialogOpen: false,
      confirmation: "",
      deleting: false,
      errorMessage: "",
      retained: [],
    };
  },
  computed: {
    // The banner belongs to the CRM only, and only while a batch exists.
    visible() {
      return Boolean(this.status?.active) && this.isCrmRoute;
    },
    isCrmRoute() {
      return String(this.$route?.path || "").startsWith("/ncrm");
    },
    keyword() {
      return this.status?.confirmationKeyword || DEFAULT_KEYWORD;
    },
    canDelete() {
      return canDeleteDemoData(this.storedRoles);
    },
    storedRoles() {
      try {
        return JSON.parse(localStorage.getItem("user-roles") || "[]");
      } catch {
        return [];
      }
    },
    // Typing the exact keyword is what unlocks the destructive action.
    confirmed() {
      return this.confirmation.trim() === this.keyword;
    },
    retainedMessage() {
      if (!this.retained.length) return "";
      return this.$t("ncrm.demoData.dialog.retained", { count: this.retained.length });
    },
  },
  watch: {
    // Entering the CRM after login must show the banner without a reload.
    isCrmRoute: {
      immediate: true,
      handler(active) {
        if (active && !this.status) this.loadStatus();
      },
    },
  },
  methods: {
    async loadStatus() {
      try {
        const { data } = await fetchDemoDataStatus();
        this.status = data || null;
      } catch {
        // A banner is not worth surfacing an error for; it stays hidden.
        this.status = null;
      }
    },
    openDialog() {
      this.confirmation = "";
      this.errorMessage = "";
      this.retained = [];
      this.dialogOpen = true;
    },
    closeDialog() {
      this.dialogOpen = false;
    },
    async submit() {
      if (!this.confirmed || this.deleting) return;
      this.deleting = true;
      this.errorMessage = "";
      try {
        const { data } = await deleteDemoData(this.confirmation.trim());
        this.status = data?.status || null;
        this.retained = data?.retained || [];
        if (data?.deleted) {
          this.dialogOpen = false;
          // Reload so every open list drops the demo rows it already fetched.
          this.$router.go(0);
        }
      } catch (error) {
        this.errorMessage =
          error?.response?.data?.error || this.$t("ncrm.demoData.errors.delete");
      } finally {
        this.deleting = false;
      }
    },
  },
};
</script>

<style scoped>
.demo-banner {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 6px 16px;
  background: rgba(var(--v-theme-warning), 0.12);
  border-bottom: 1px solid rgba(var(--v-theme-warning), 0.28);
  color: var(--app-text-strong);
  font-size: 0.82rem;
  line-height: 1.4;
}

.demo-banner__icon {
  color: rgb(var(--v-theme-warning));
  flex-shrink: 0;
}

.demo-banner__text {
  min-width: 0;
}

.demo-banner__action {
  flex-shrink: 0;
  font-weight: 600;
}

@media (max-width: 599px) {
  .demo-banner {
    flex-wrap: wrap;
    justify-content: flex-start;
    text-align: start;
  }
}
</style>
